import { Component, forwardRef, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { COUNTRIES, Country, findCountry } from '../../countries';

let nextId = 0;

@Component({
  selector: 'app-country-select',
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatAutocompleteModule],
  templateUrl: './country-select.html',
  styleUrl: './country-select.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelect),
      multi: true,
    },
  ],
})
export class CountrySelect implements ControlValueAccessor {
  @Input() label = 'Country';
  @Input() placeholder = 'Search by name or code...';
  @Input() icon = '';
  @Input() variant: 'default' | 'glass' = 'default';

  inputId = `country-select-${nextId++}`;

  value = '';
  disabled = false;

  // Glass variant only: its own hand-rolled dropdown/keyboard nav (unchanged).
  isOpen = signal(false);
  query = signal('');
  highlightedIndex = signal(0);

  // Default variant only: a real mat-autocomplete, same pattern as add-participant-dialog's
  // company field — Material owns the dropdown, keyboard nav and open/close state itself.
  matSelection: Country | string = '';

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  get filtered(): Country[] {
    const q = this.query().trim().toLowerCase();
    if (!q) {
      return COUNTRIES;
    }
    return COUNTRIES.filter(
      (c) => c.code.toLowerCase().startsWith(q) || c.name.toLowerCase().includes(q)
    );
  }

  get displayValue(): string {
    if (this.isOpen()) {
      return this.query();
    }
    const country = findCountry(this.value);
    return country ? `${country.name} (${country.code})` : '';
  }

  get filteredCountries(): Country[] {
    const term = typeof this.matSelection === 'string' ? this.matSelection.trim().toLowerCase() : '';
    if (!term) {
      return COUNTRIES;
    }
    return COUNTRIES.filter(
      (c) => c.code.toLowerCase().startsWith(term) || c.name.toLowerCase().includes(term)
    );
  }

  displayCountry = (value: Country | string): string => {
    if (!value) {
      return '';
    }
    return typeof value === 'string' ? value : `${value.name} (${value.code})`;
  };

  onOptionSelected(country: Country): void {
    this.matSelection = country;
    this.value = country.code;
    this.onChange(this.value);
    this.onTouched();
  }

  writeValue(value: string): void {
    this.value = value ?? '';
    this.matSelection = findCountry(this.value) ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFocus(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen.set(true);
    this.query.set('');
    this.highlightedIndex.set(0);
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.query.set(input.value);
    this.isOpen.set(true);
    this.highlightedIndex.set(0);
  }

  onBlur(): void {
    this.isOpen.set(false);
    this.onTouched();
  }

  onKeydown(event: KeyboardEvent): void {
    const options = this.filtered;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.isOpen.set(true);
      this.highlightedIndex.set(Math.min(this.highlightedIndex() + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex.set(Math.max(this.highlightedIndex() - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const selected = options[this.highlightedIndex()];
      if (selected) {
        this.selectCountry(selected);
      }
    } else if (event.key === 'Escape') {
      this.isOpen.set(false);
    }
  }

  selectCountry(country: Country): void {
    this.value = country.code;
    this.isOpen.set(false);
    this.query.set('');
    this.onChange(this.value);
    this.onTouched();
  }
}
